import React, { useEffect, useState, useMemo } from "react";

// UX
import CardMedCertificateReportUX from "./CardMedCertificateReportUX";

// Interface
import { State } from "./sequence/DoctorCertificate";

// Utils
import moment from "moment";
import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";
import { Button, Dimmer, Dropdown, FormGroup, Loader, Form } from "semantic-ui-react";

// Common
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Card
import CardDeathCertificate from "./CardDeathCertificate";
import CardFiveDiseasesCertificate from "./CardFiveDiseasesCertificate";
import CardExamAbsenceCertificate from "./CardExamAbseneCertifiate";
import CardDrivingCertificate from "./CardDrivingCertificate";
import CardQuarantineCertificate from "./CardQuarantineCertificate";
import CardCovidCertificate from "./CardCovidCertificate";
import CardTravelCertificate from "./CardTravelCertificate";
import CardDefaultCertificate from "./CardDefaultCertificate";
import CardVisaExtensionCertificate from "./CardVisaExtensionCertificate";
import CardDentistCertificate from "./CardDentistCertificate";
import { useIntl } from "react-intl";

// Main Component Props
type CardMedCertificateProps = {
  onEvent: any;
  setProp: any;
  runSequence?: any;
  DoctorCertificateSequence: State["DoctorCertificateSequence"];
  masterOptions?: any;
  errorMessage: any;
  loadingStatus: any;
  languageUX: "en" | "th";
};

// Function

// Current Date
const defaultDate = dateToStringWithoutTimeBE(moment());

// Options
const langOptions = [
  { key: 1, value: "ไทย", text: "ไทย" },
  { key: 2, value: "EN", text: "EN" },
];

// Main
const CardMedCertificateReport = (props: CardMedCertificateProps) => {
  const intl = useIntl();
  const [checkAllDoctor, setCheckAllDoctor] = useState<boolean>(false);
  // Call Sequence ( Start Seq )
  useEffect(() => {
    props.runSequence({
      sequence: "DoctorCertificate",
      // restart: true,
    });
  }, []);

  const formCodeMap = useMemo(
    () => props.DoctorCertificateSequence?.formCodeMap || {},
    [props.DoctorCertificateSequence]
  );

  const isCopied = useMemo(() => {
    const config = props.DoctorCertificateSequence?.medcertConfig;
    if (!config) return false;

    return !config.isCreate && !config.isEdit;
  }, [props.DoctorCertificateSequence?.medcertConfig]);

  // For SetProp
  const reportLanguageSetter: Record<string, string> = {
    [formCodeMap.CardDeathCertificate]: "deathReport",
    [formCodeMap.CardFiveDiseasesCertificate]: "fiveReport",
    // [formCodeMap.CardExamAbsenceCertificate]: "examAbsenceReport",
    [formCodeMap.CardDrivingCertificate]: "drivingReport",
    [formCodeMap.CardQuarantineCertificate]: "quarantineReport",
    [formCodeMap.CardCovidCertificate]: "covidReport",
    [formCodeMap.CardTravelCertificate]: "travelReport",
    [formCodeMap.CardVisaExtensionCertificate]: "visaExtReport",
    [formCodeMap.CardDentistCertificate]: "dentistReport",
  };

  // Call Get Report Seqeunce
  // console.log("CertReport Report Option: ", props.DoctorCertificateSequence?.reportOption);
  // console.log("CertReport Report List: ", props.DoctorCertificateSequence?.reportList);

  // All Props Value
  // console.log("Props DoctorCertificate: ", props.DoctorCertificateSequence);

  // Change Date
  const handleChangeDateNew = (name: any, date: string) => {
    switch (name) {
      case "startDate":
      case "endDate":
        props.setProp(`DoctorCertificateSequence.filterReport.${name}`, date);
        break;
      default:
        console.log("Doesn't Exist!!");
        break;
    }
  };

  // Language Value
  const handleLanguageValue = (reportType: any) => {
    if (reportType === formCodeMap.CardDeathCertificate) {
      return props.DoctorCertificateSequence?.deathReport?.reportLanguage;
    } else if (reportType === formCodeMap.CardFiveDiseasesCertificate) {
      return props.DoctorCertificateSequence?.fiveReport?.reportLanguage;
    } else if (reportType.startsWith(formCodeMap.CardExamAbsenceCertificate)) {
      return props.DoctorCertificateSequence?.examAbsenceReport?.reportLanguage;
    } else if (reportType === formCodeMap.CardDrivingCertificate) {
      return props.DoctorCertificateSequence?.drivingReport?.reportLanguage;
    } else if (reportType === formCodeMap.CardQuarantineCertificate) {
      return props.DoctorCertificateSequence?.quarantineReport?.reportLanguage;
    } else if (reportType === formCodeMap.CardCovidCertificate) {
      return props.DoctorCertificateSequence?.covidReport?.reportLanguage;
    } else if (reportType === formCodeMap.CardTravelCertificate) {
      return props.DoctorCertificateSequence?.travelReport?.reportLanguage;
    } else if (reportType === formCodeMap.CardVisaExtensionCertificate) {
      return props.DoctorCertificateSequence?.visaExtReport?.reportLanguage;
    } else if (reportType === formCodeMap.CardDentistCertificate) {
      return props.DoctorCertificateSequence?.dentistReport?.reportLanguage;
    } else {
      return props.DoctorCertificateSequence?.filterReport?.reportLanguage;
    }
  };

  // Change Language
  const handleChangeLanguage = (language: string) => {
    const option = props.DoctorCertificateSequence?.reportOption;

    if (!option) return;

    if (option.startsWith(formCodeMap.CardExamAbsenceCertificate)) {
      props.setProp(`DoctorCertificateSequence.examAbsenceReport.reportLanguage`, language);
      return;
    }

    const reportKey = reportLanguageSetter[option] ?? "filterReport";

    props.setProp(`DoctorCertificateSequence.${reportKey}.reportLanguage`, language);
  };

  // Change Report
  const handleChangeReport = (report: string) => {
    props.setProp(`DoctorCertificateSequence.reportOption`, report);
  };

  // get Value from Report Dropdown
  const handleSelectedReport = (e: any, { value }: { value: string }) => {
    // console.log("Yeti Handle Select Report: ", value) // formCodeMap.CardDeathCertificate, "Test1"
    if (value === "") {
      value = "default";
    }
    handleChangeReport(value);
  };

  const reportTypeSelect = useMemo(() => {
    const reportOption = props.DoctorCertificateSequence?.reportOption;

    if (reportOption?.startsWith(formCodeMap.CardExamAbsenceCertificate)) {
      return (
        <CardExamAbsenceCertificate
          type={reportOption}
          runSequence={props.runSequence}
          setProp={props.setProp}
          DoctorCertificateSequence={props.DoctorCertificateSequence}
          masterOptions={props.masterOptions}
          languageUX={props.languageUX}
        />
      );
    }

    switch (reportOption) {
      case formCodeMap.CardDeathCertificate:
        return (
          <CardDeathCertificate
            runSequence={props.runSequence}
            setProp={props.setProp}
            DoctorCertificateSequence={props.DoctorCertificateSequence}
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        );
      case formCodeMap.CardFiveDiseasesCertificate:
        return (
          <CardFiveDiseasesCertificate
            runSequence={props.runSequence}
            setProp={props.setProp}
            DoctorCertificateSequence={props.DoctorCertificateSequence}
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        );
      // case formCodeMap.CardExamAbsenceCertificate:
      //   return (
      //     <CardExamAbsenceCertificate
      //       runSequence={props.runSequence}
      //       setProp={props.setProp}
      //       DoctorCertificateSequence={props.DoctorCertificateSequence}
      //       masterOptions={props.masterOptions}
      //       languageUX={props.languageUX}
      //     />
      //   );
      case formCodeMap.CardDrivingCertificate:
        return (
          <CardDrivingCertificate
            runSequence={props.runSequence}
            setProp={props.setProp}
            DoctorCertificateSequence={props.DoctorCertificateSequence}
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        );
      case formCodeMap.CardQuarantineCertificate:
        return (
          <CardQuarantineCertificate
            runSequence={props.runSequence}
            setProp={props.setProp}
            DoctorCertificateSequence={props.DoctorCertificateSequence}
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        );
      case formCodeMap.CardCovidCertificate:
        return (
          <CardCovidCertificate
            runSequence={props.runSequence}
            setProp={props.setProp}
            DoctorCertificateSequence={props.DoctorCertificateSequence}
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        );
      case formCodeMap.CardTravelCertificate:
        return (
          <CardTravelCertificate
            runSequence={props.runSequence}
            setProp={props.setProp}
            DoctorCertificateSequence={props.DoctorCertificateSequence}
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        );
      case formCodeMap.CardVisaExtensionCertificate:
        return (
          <CardVisaExtensionCertificate
            runSequence={props.runSequence}
            setProp={props.setProp}
            DoctorCertificateSequence={props.DoctorCertificateSequence}
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        );
      case formCodeMap.CardDentistCertificate:
        return (
          <CardDentistCertificate
            runSequence={props.runSequence}
            setProp={props.setProp}
            DoctorCertificateSequence={props.DoctorCertificateSequence}
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        );
      case "default":
        return (
          <CardDefaultCertificate
            runSequence={props.runSequence}
            setProp={props.setProp}
            DoctorCertificateSequence={props.DoctorCertificateSequence}
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        );
      default:
        return null;
    }
  }, [props.DoctorCertificateSequence]);

  // Change Doctor Input
  const handleChangeDoctor = (name: any, e: React.ChangeEvent<HTMLInputElement>) => {
    switch (name) {
      case "doctorName":
        props.setProp(`DoctorCertificateSequence.filterReport.${name}`, e.target.value);
        break;
      default:
        console.log("Doesn't Exist!!");
        break;
    }
  };

  // Call Sequence ( Save Report )
  const handleSaveReport = () => {
    props.runSequence({
      sequence: "DoctorCertificate",
      action: "SAVE_REPORT",
      // formCode: formCodeMap[props.DoctorCertificateSequence?.reportOption]
    });
  };

  // Call Sequence ( Preview Report )
  const handlePreviewReport = () => {
    props.runSequence({
      sequence: "DoctorCertificate",
      action: "PREVIEW_REPORT",
    });
  };

  // Call Sequence ( Search Report )
  const handleSearchReport = () => {
    props.runSequence({
      sequence: "DoctorCertificate",
      action: "SEARCH_REPORT",
      checkAllDoctor: checkAllDoctor,
    });
  };

  // Call Sequence ( Re-Print Report )
  const handleRePrintReport = (reportType: any, options: "edit" | "reprint") => {
    // console.log("Now you're clicking Re-Print Button");

    const checkReportType =
      reportType === formCodeMap.CardDeathCertificate ||
      reportType === formCodeMap.CardFiveDiseasesCertificate ||
      reportType === formCodeMap.CardDrivingCertificate ||
      reportType === formCodeMap.CardQuarantineCertificate ||
      reportType === formCodeMap.CardCovidCertificate ||
      reportType === formCodeMap.CardTravelCertificate ||
      reportType === formCodeMap.CardVisaExtensionCertificate ||
      reportType === formCodeMap.CardDentistCertificate ||
      reportType?.startsWith(formCodeMap.CardExamAbsenceCertificate); // <== รองรับ 2,3,4,...

    if (checkReportType && props.DoctorCertificateSequence?.selectedReport?.id) {
      console.log("Form ID", props.DoctorCertificateSequence?.selectedReport?.id);

      // Call Sequence ( Reprint Report )
      props.runSequence({
        sequence: "DoctorCertificate",
        action: "REPRINT_REPORT",
        options,
      });
    } else {
      console.error("Form ID Not Found!!");
    }
  };

  // Disable Report Type Select
  const handleDisableForm = (selected: any) => {
    if (!props.DoctorCertificateSequence?.isDoctor) {
      return true;
    }

    return selected !== null;
  };

  const handleClearSelectData = () => {
    const reportOption = props.DoctorCertificateSequence?.reportOption;
    // Set selectedReport = null
    props.setProp(`DoctorCertificateSequence.selectedReport`, null);

    // Re-Fetch
    props.runSequence({
      sequence: "DoctorCertificate",
      action: "CLEAR_REPORT",
      callReport: formCodeMap[reportOption || ""],
    });
  };

  // Report Language and Save
  const saveReport = (selected: any) => {
    // Select Report Check
    if (selected == null) {
      const reportOption = props.DoctorCertificateSequence?.reportOption;

      const shouldShowLanguageSection =
        reportOption === formCodeMap.CardDeathCertificate ||
        reportOption === formCodeMap.CardFiveDiseasesCertificate ||
        reportOption === formCodeMap.CardDrivingCertificate ||
        reportOption === formCodeMap.CardQuarantineCertificate ||
        reportOption === formCodeMap.CardCovidCertificate ||
        reportOption === formCodeMap.CardTravelCertificate ||
        reportOption === formCodeMap.CardVisaExtensionCertificate ||
        reportOption === formCodeMap.CardDentistCertificate ||
        reportOption?.startsWith(formCodeMap.CardExamAbsenceCertificate); // <== รองรับ 2,3,4,...

      if (shouldShowLanguageSection) {
        return (
          <Form>
            <FormGroup className="ui form" style={{ justifyContent: "flex-end", gap: "1em" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: "bold",
                }}
              >
                ภาษา
              </label>
              <Dropdown
                onChange={(e, { value }) => handleChangeLanguage(value as string)}
                options={langOptions}
                selection
                value={handleLanguageValue(reportOption)}
                style={{ minWidth: "6em" }}
              />
              <Button color="green" onClick={handleSaveReport} style={{ width: "10em" }}>
                บันทึกและพิมพ์
              </Button>
            </FormGroup>
          </Form>
        );
      }

      return null;
      // switch (props.DoctorCertificateSequence?.reportOption) {
      //   case formCodeMap.CardDeathCertificate:
      //   case formCodeMap.CardFiveDiseasesCertificate:
      //   case formCodeMap.CardExamAbsenceCertificate:
      //   case formCodeMap.CardDrivingCertificate:
      //   case formCodeMap.CardQuarantineCertificate:
      //   case formCodeMap.CardCovidCertificate:
      //   case formCodeMap.CardTravelCertificate:
      //   case formCodeMap.CardVisaExtensionCertificate:
      //   case formCodeMap.CardDentistCertificate:
      //     return (
      //       <Form>
      //         <FormGroup className="ui form" style={{ justifyContent: "flex-end", gap: "1em" }}>
      //           <label
      //             style={{
      //               display: "flex",
      //               alignItems: "center",
      //               fontWeight: "bold",
      //             }}
      //           >
      //             ภาษา
      //           </label>
      //           <Dropdown
      //             onChange={(e, { value }) => handleChangeLanguage(value)}
      //             options={langOptions}
      //             selection={true}
      //             value={handleLanguageValue(props.DoctorCertificateSequence?.reportOption)}
      //             style={{ minWidth: "6em" }}
      //           ></Dropdown>
      //           <Button
      //             color="green"
      //             onClick={handleSaveReport}
      //             style={{ width: "10em" }}
      //           >
      //             บันทึกและพิมพ์
      //           </Button>
      //         </FormGroup>
      //       </Form>
      //     );
      //   case "default":
      //   default:
      //     return null;
      // }
    } else {
      return (
        <Form style={{ marginTop: "-1rem" }}>
          <FormGroup className="ui form" style={{ justifyContent: "flex-end", gap: "1em" }}>
            {hideEditNPrintBtn()}
            <Button color="red" style={{ width: "8em" }} onClick={handleClearSelectData}>
              ปิด
            </Button>
          </FormGroup>
        </Form>
      );
    }
  };

  const hideEditNPrintBtn = () => {
    const printFormEncounter = props.DoctorCertificateSequence?.selectedReport?.encounter;

    if (printFormEncounter === props.DoctorCertificateSequence?.currentEncounter && !isCopied) {
      return (
        <Button
          color="yellow"
          onClick={(e) =>
            handleRePrintReport(props.DoctorCertificateSequence?.reportOption, "edit")
          }
          style={{ width: "10em" }}
        >
          แก้ไขและพิมพ์
        </Button>
      );
    }
    return;
  };

  // Re-print Option
  const duplicateReport = (selected: any) => {
    // Select Report Check
    if (selected == null) {
      return (
        <Form>
          <FormGroup
            className="ui form"
            style={{
              justifyContent: "flex-end",
              padding: "2em 0em 0em 0em",
              gap: "1em",
            }}
          >
            <Button color="yellow" onClick={handlePreviewReport} style={{ width: "10em" }}>
              Preview
            </Button>
          </FormGroup>
        </Form>
      );
    } else {
      return (
        <Form>
          <FormGroup className="ui form" style={{ padding: "2em 0em 0em 4em", gap: "1em" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                fontWeight: "bold",
              }}
            >
              เลือกภาษาเพื่อพิมพ์ซ้ำ
            </label>
          </FormGroup>
          <FormGroup className="ui form" style={{ justifyContent: "flex-end", gap: "1em" }}>
            <Dropdown
              disabled={isCopied}
              placeholder={intl.formatMessage({ id: "โปรดเลือกภาษาของเอกสาร" })}
              selection={true}
              onChange={(e, { value }) => handleChangeLanguage(value as string)}
              options={langOptions}
              // value={handleLanguageValue(
              //   props.DoctorCertificateSequence?.reportOption
              // )}
            ></Dropdown>
            <Button
              color="green"
              onClick={(e) =>
                handleRePrintReport(props.DoctorCertificateSequence?.reportOption, "reprint")
              }
              style={{
                width: "10em",
              }}
            >
              พิมพ์ซ้ำ
            </Button>
            <Button color="yellow" onClick={handlePreviewReport} style={{ width: "10em" }}>
              Preview
            </Button>
          </FormGroup>
        </Form>
      );
    }
  };

  // isDoctor Check
  const handleDoctorCheck = (isDoctor: any) => {
    if (isDoctor === false) {
      return true;
    } else {
      return false;
    }
  };

  const handleCheckAllDoctor = () => {
    setCheckAllDoctor(!checkAllDoctor);
  };

  // console.log("Yeti Med Cert Props: ", props);

  return (
    <>
      <SnackMessage
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp("errorMessage.DoctorCertificate", null);
        }}
        error={props.errorMessage?.DoctorCertificate}
        success={null}
      />

      <CardMedCertificateReportUX
        // Report Option Props
        // reportOption={reportOptions}
        checkAllDoctor={checkAllDoctor}
        onCheckAllDoctor={handleCheckAllDoctor}
        reportOption={props.DoctorCertificateSequence?.reportTypeList || []}
        onReportValue={
          props.DoctorCertificateSequence?.reportOption === "default"
            ? ""
            : props.DoctorCertificateSequence?.reportOption || ""
        }
        onHandleReport={handleSelectedReport}
        // Date Props
        startDate={props.DoctorCertificateSequence?.filterReport?.startDate || defaultDate}
        endDate={props.DoctorCertificateSequence?.filterReport?.endDate || defaultDate}
        disabledReportOption={props.DoctorCertificateSequence?.selectedReport}
        onHandleDateNew={handleChangeDateNew}
        // Doctor Props
        onDoctorName={props.DoctorCertificateSequence?.filterReport?.doctorName || ""}
        onHandleDoctorName={handleChangeDoctor}
        // Table Data
        // keyTable={`created, encounter, data.reportType, data.reportLanguage, data.doctorName, data.reportDuplicate`}
        // keyTable={`edited, statable, form_type_name, tracked_data.reportLanguage, name, printCount`}
        keyTable={`edited, statable, form_type_name, reportLanguage, name, printCount`}
        dataTable={props.DoctorCertificateSequence?.reportList?.map((item: any) => ({
          ...item,
          printCount:
            item.tracked_data?.printCount > 1 || item.tracked_data?.print_count > 1
              ? "สำเนา"
              : "ต้นฉบับ",
          reportLanguage:
            (item.tracked_data?.language === "TH" ? "ไทย" : item.tracked_data?.language) ||
            (item.tracked_data?.reportLanguage === "TH"
              ? "ไทย"
              : item.tracked_data?.reportLanguage),
        }))}
        rowProps={(state: any, rowInfo: any, column: any, instance: any) => ({
          style: {
            backgroundColor:
              props.DoctorCertificateSequence?.selectedReport &&
              rowInfo?.original?.statable === props.DoctorCertificateSequence?.selectedReport?.id &&
              rowInfo?.original?.id === props.DoctorCertificateSequence?.selectedReport?.logId
                ? "#a5dae6"
                : "white",
          },

          onClick: (e: any) => {
            console.log("SELECT Click rowInfo: ", rowInfo?.original);
            console.log("SELECT Click original id: ", rowInfo?.original?.id);
            console.log(
              "SELECT Click selectedReport id: ",
              props.DoctorCertificateSequence?.selectedReport?.id
            );
            // console.log(
            //   "SELECT Click reportType: ",
            //   rowInfo?.original?.data?.reportType
            // );
            console.log("SELECT Click id Form: ", rowInfo?.original?.statable);
            // console.log("SELECT Click reportType: ", props.DoctorCertificateSequence?.selectedReport?.id)

            props.runSequence({
              sequence: "DoctorCertificate",
              action: "SELECT_DATA",
              selectedItem: rowInfo?.original,
              selectedReport: rowInfo?.original?.statable,
              selectedReportId: rowInfo?.original?.id,
            });
          },
        })}
        // Language Option Props
        langOptions={langOptions}
        onLanguageValue={handleLanguageValue(props.DoctorCertificateSequence?.reportOption)}
        onHandleLanguage={handleChangeLanguage}
        // Save and Print Certificate [Component]
        onSaveReport={saveReport(props.DoctorCertificateSequence?.selectedReport)}
        // Preview Certificate
        onPreviewReport={handlePreviewReport}
        // Search Certificate
        onSearchReport={handleSearchReport}
        // Certificate Report Props [Component]
        reportTypeSelect={
          // Add new Report at this Function
          reportTypeSelect
        }
        // Disable Report Type Option
        onHandleDisableForm={handleDisableForm(null)}
        // isDoctor Check for Report Type Option
        onHandleDoctorCheck={handleDoctorCheck(props.DoctorCertificateSequence?.isDoctor)}
        // Re-Print [Component]
        onDuplicateReport={duplicateReport(props.DoctorCertificateSequence?.selectedReport)}
        searchReportLoading={props.DoctorCertificateSequence?.searchReportLoading}
        onLoading={
          <Dimmer active={props.loadingStatus?.["DoctorCertificate"]} inverted>
            <Loader inverted>Loading</Loader>
          </Dimmer>
        }
        languageUX={props.languageUX}
      />
    </>
  );
};

export default CardMedCertificateReport;
